import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2 } from "lucide-react";
import { LocationPicker, type LocationData } from "./LocationPicker";
import { TimePicker } from "./TimePicker";

interface AddHabitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (habit: HabitFormData) => Promise<void>;
}

export interface HabitFormData {
  name: string;
  frequencyType: "daily" | "weekly" | "monthly" | "yearly";
  frequencyCount: number;
  evidenceType: "photo" | "confirmation" | "location" | "time" | "none";
  isPrivate: boolean;
  locationData?: LocationData | null;
  scheduledTime?: string | null;
  timezone?: string | null;
}

export const AddHabitDialog = ({ open, onOpenChange, onSubmit }: AddHabitDialogProps) => {
  const [name, setName] = useState("");
  const [frequencyType, setFrequencyType] = useState<HabitFormData["frequencyType"]>("daily");
  const [frequencyCount, setFrequencyCount] = useState(1);
  const [evidenceType, setEvidenceType] = useState<HabitFormData["evidenceType"]>("none");
  const [isPrivate, setIsPrivate] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [scheduledTime, setScheduledTime] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (evidenceType === "location" && !locationData) return;
    if (evidenceType === "time" && !scheduledTime) return;

    setIsLoading(true);
    try {
      await onSubmit({
        name: name.trim(),
        frequencyType,
        frequencyCount,
        evidenceType,
        isPrivate,
        locationData: evidenceType === "location" ? locationData : null,
        scheduledTime: evidenceType === "time" ? scheduledTime : null,
        timezone: evidenceType === "time" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
      });
      // Reset form
      setName("");
      setFrequencyType("daily");
      setFrequencyCount(1);
      setEvidenceType("none");
      setIsPrivate(true);
      setLocationData(null);
      setScheduledTime(null);
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  };

  const frequencyLabels = {
    daily: "dia(s)",
    weekly: "semana(s)",
    monthly: "mês(es)",
    yearly: "ano(s)",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card border-border max-h-[90vh] overflow-y-auto p-6 sm:p-6">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-2xl font-display text-center flex items-center justify-center gap-2">
            <span>🥚</span> Novo Hábito
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">{/* Name */}
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="font-semibold">
              Nome do hábito
            </Label>
            <Input
              id="name"
              placeholder="Ex: Meditar, Exercitar, Ler..."
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={50}
            />
          </div>

          {/* Frequency Type */}
          <div className="space-y-3">
            <Label className="font-semibold">Frequência</Label>
            <RadioGroup
              value={frequencyType}
              onValueChange={(v) => setFrequencyType(v as HabitFormData["frequencyType"])}
              className="grid grid-cols-2 gap-2"
            >
              {(["daily", "weekly", "monthly", "yearly"] as const).map((type) => (
                <div key={type} className="flex items-center space-x-2">
                  <RadioGroupItem value={type} id={type} />
                  <Label htmlFor={type} className="cursor-pointer capitalize">
                    {type === "daily" && "Diário"}
                    {type === "weekly" && "Semanal"}
                    {type === "monthly" && "Mensal"}
                    {type === "yearly" && "Anual"}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Frequency Count */}
          <div className="space-y-2">
            <Label htmlFor="count" className="font-semibold">
              Quantas vezes por {frequencyLabels[frequencyType].replace("(s)", "")}?
            </Label>
            <Select
              value={frequencyCount.toString()}
              onValueChange={(v) => setFrequencyCount(parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <SelectItem key={n} value={n.toString()}>
                    {n} vez{n > 1 ? "es" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Evidence Type */}
          <div className="space-y-3">
            <Label className="font-semibold">Tipo de evidência</Label>
            <RadioGroup
              value={evidenceType}
              onValueChange={(v) => setEvidenceType(v as HabitFormData["evidenceType"])}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="none" id="none" />
                <Label htmlFor="none" className="cursor-pointer">Nenhuma</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="photo" id="photo" />
                <Label htmlFor="photo" className="cursor-pointer">📷 Foto</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="confirmation" id="confirmation" />
                <Label htmlFor="confirmation" className="cursor-pointer">👥 Confirmação de outro usuário</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="location" id="location" />
                <Label htmlFor="location" className="cursor-pointer">📍 Localização</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="time" id="time" />
                <Label htmlFor="time" className="cursor-pointer">⏰ Horário</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Location picker - shown when location evidence is selected */}
          {evidenceType === "location" && (
            <LocationPicker value={locationData} onChange={setLocationData} />
          )}

          {/* Time picker - shown when time evidence is selected */}
          {evidenceType === "time" && (
            <TimePicker value={scheduledTime} onChange={setScheduledTime} />
          )}

          {/* Privacy */}
          <div className="flex items-center justify-between">
            <Label htmlFor="private" className="font-semibold">
              Hábito privado
            </Label>
            <Switch
              id="private"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
            />
          </div>

          <Button
            type="submit"
            variant="coral"
            size="lg"
            className="w-full mt-6 mb-2"
            disabled={isLoading || !name.trim() || (evidenceType === "location" && !locationData) || (evidenceType === "time" && !scheduledTime)}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <>Criar hábito 🥚</>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
